import React, { FunctionComponent, ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AvailableLocationInfo,
  LocationInfoType
} from 'record-it-utils/location/GeolocationManager/GeolocationManager';
import { useLocationInfo } from '../../hooks/useLocationInfo/useLocationInfo';
import { LoadingOverlay } from '../../misc/LoadingOverlay/LoadingOverlay';
import { MessageBox, MessageBoxType } from '../../misc/MessageBox/MessageBox';
import { StrictReactNode } from '../../types/StrictReactNode';
import { prepareProps } from '../../utils/propUtils';
import { classes } from '../../utils/utils';
import styles from './LocationContentContainer.module.css';

export type LocationContentContainerProps = Omit<
  ComponentProps<'div'>,
  'children'
> & {
  children: (options: {
    locationInfo: AvailableLocationInfo;
  }) => StrictReactNode;
};

/**
 * A container which only renders the content if a location is available.
 * If no location is available, an error message will be shown.
 */
export const LocationContentContainer: FunctionComponent<LocationContentContainerProps> =
  React.memo((props) => {
    const { children, className, ...remainingProps } = prepareProps({ props });
    const locationInfo = useLocationInfo();

    const { t } = useTranslation('record-it-components');

    return (
      <div
        {...remainingProps}
        className={classes(className, styles.container)}
      >
        {locationInfo.type === LocationInfoType.Available
          ? children({ locationInfo })
          : null}

        {locationInfo.type === LocationInfoType.Pending ? (
          <div className={styles.noContentPlaceholder}></div>
        ) : undefined}

        {locationInfo.type === LocationInfoType.NoPermission ? (
          <MessageBox
            type={MessageBoxType.Error}
            data-testid="LocationContentContainer.MessageBox"
          >
            {t('location.LocationContentContainer.noPermissionMessage')}
          </MessageBox>
        ) : undefined}

        {locationInfo.type === LocationInfoType.Unavailable ? (
          <MessageBox
            type={MessageBoxType.Warning}
            data-testid="LocationContentContainer.MessageBox"
          >
            {t('location.LocationContentContainer.unavailableMessage')}
          </MessageBox>
        ) : undefined}

        <LoadingOverlay
          active={locationInfo.type === LocationInfoType.Pending}
          data-testid="LocationContentContainer.LoadingOverlay"
        ></LoadingOverlay>
      </div>
    );
  });
